import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Database, FileUp, Check } from 'lucide-react';

const INDUSTRIES = [
  { id: 'logistics', label: 'Logistics & Supply Chain', icon: '🚚' },
  { id: 'ecommerce', label: 'E-commerce & Retail', icon: '🛒' },
  { id: 'finance', label: 'Finance & Banking', icon: '💰' },
  { id: 'realestate', label: 'Real Estate', icon: '🏠' },
  { id: 'tech', label: 'Technology / SaaS', icon: '💻' },
  { id: 'healthcare', label: 'Healthcare', icon: '🏥' },
];

const DATA_SOURCES = [
  { id: 'crm', label: 'CRM Integration', description: 'Connect HubSpot, Zoho, or Salesforce', icon: Database },
  { id: 'file_upload', label: 'File Upload (CSV / Excel)', description: 'Upload historical data files', icon: FileUp },
];

interface Props {
  industry: string | null;
  dataSourceType: string | null;
  onUpdate: (industry: string, dataSourceType: string) => void;
}

export const IndustryDataSourceStep = ({ industry, dataSourceType, onUpdate }: Props) => {
  const selectedIndustry = industry || '';
  const selectedSource = dataSourceType || '';

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">Select Your Industry</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          This helps the AI tailor cleaning rules, predictions, and dashboards to your business.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {INDUSTRIES.map((ind) => (
            <Card
              key={ind.id}
              className={`p-4 cursor-pointer transition-all hover:border-primary/50 ${
                selectedIndustry === ind.id ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : ''
              }`}
              onClick={() => onUpdate(ind.id, selectedSource)}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">{ind.icon}</span>
                <span className="text-sm font-medium">{ind.label}</span>
                {selectedIndustry === ind.id && <Check className="h-4 w-4 text-primary ml-auto" />}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <Database className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">Choose Your Data Source</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Where does your business data live? The AI will inspect and remember its structure.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {DATA_SOURCES.map((src) => (
            <Card
              key={src.id}
              className={`p-4 cursor-pointer transition-all hover:border-primary/50 ${
                selectedSource === src.id ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : ''
              }`}
              onClick={() => onUpdate(selectedIndustry, src.id)}
            >
              <div className="flex items-center gap-3">
                <src.icon className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-medium text-sm">{src.label}</p>
                  <p className="text-xs text-muted-foreground">{src.description}</p>
                </div>
                {selectedSource === src.id && <Check className="h-4 w-4 text-primary ml-auto" />}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
